/** Small deterministic JSON Schema validator. The exported schemas are draft 2020-12. */
export class ValidationError extends Error {
  constructor(issues) {
    super(issues.map((i) => `${i.path}: ${i.message}`).join("; "));
    this.name = "ValidationError";
    this.issues = issues;
  }
}
export const forbiddenKeys = new Set(["__proto__", "prototype", "constructor"]);
export function deepFreeze(value) {
  if (value && typeof value === "object") {
    for (const v of Object.values(value)) deepFreeze(v);
    Object.freeze(value);
  }
  return value;
}
export function assertSafe(value, path = "$", depth = 0) {
  if (depth > 20)
    throw new ValidationError([
      { path, message: "Maximum object depth is 20" },
    ]);
  if (typeof value === "number" && !Number.isFinite(value))
    throw new ValidationError([{ path, message: "Numbers must be finite" }]);
  if (value && typeof value === "object") {
    if (
      !Array.isArray(value) &&
      Object.getPrototypeOf(value) !== Object.prototype &&
      Object.getPrototypeOf(value) !== null
    )
      throw new ValidationError([
        { path, message: "Expected plain JSON object" },
      ]);
    for (const key of Object.keys(value)) {
      if (forbiddenKeys.has(key))
        throw new ValidationError([
          { path: `${path}.${key}`, message: "Reserved key" },
        ]);
      assertSafe(value[key], `${path}.${key}`, depth + 1);
    }
  } else if (
    ["function", "symbol", "bigint", "undefined"].includes(typeof value)
  )
    throw new ValidationError([{ path, message: "Expected JSON value" }]);
}
export function validate(schema, value, path = "$", issues = []) {
  const issue = (message) => issues.push({ path, message });
  if (
    schema.enum &&
    !schema.enum.some((v) => JSON.stringify(v) === JSON.stringify(value))
  )
    issue(`Must be one of ${schema.enum.join(", ")}`);
  if (schema.type === "object") {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      issue("Expected object");
      return issues;
    }
    const keys = Object.keys(value);
    if (schema.maxProperties && keys.length > schema.maxProperties)
      issue("Too many properties");
    for (const key of schema.required ?? [])
      if (!(key in value))
        issues.push({ path: `${path}.${key}`, message: "Required" });
    for (const key of keys) {
      const child = schema.properties?.[key];
      if (forbiddenKeys.has(key))
        issues.push({ path: `${path}.${key}`, message: "Reserved key" });
      else if (child) validate(child, value[key], `${path}.${key}`, issues);
      else if (schema.additionalProperties === false)
        issues.push({ path: `${path}.${key}`, message: "Unsupported field" });
      else if (
        schema.additionalProperties &&
        typeof schema.additionalProperties === "object"
      )
        validate(
          schema.additionalProperties,
          value[key],
          `${path}.${key}`,
          issues,
        );
    }
  } else if (schema.type === "array") {
    if (!Array.isArray(value)) {
      issue("Expected array");
      return issues;
    }
    if (
      value.length < (schema.minItems ?? 0) ||
      value.length > (schema.maxItems ?? 100)
    )
      issue("Array length outside supported limits");
    value.forEach((v, i) =>
      validate(schema.items ?? {}, v, `${path}[${i}]`, issues),
    );
  } else if (schema.type === "number" || schema.type === "integer") {
    if (
      typeof value !== "number" ||
      !Number.isFinite(value) ||
      (schema.type === "integer" && !Number.isInteger(value))
    )
      issue(`Expected finite ${schema.type}`);
    else if (
      value < (schema.minimum ?? -Infinity) ||
      value > (schema.maximum ?? Infinity)
    )
      issue("Outside supported bounds");
  } else if (schema.type === "string") {
    if (typeof value !== "string") issue("Expected string");
    else if (
      value.length < (schema.minLength ?? 0) ||
      value.length > (schema.maxLength ?? 4096) ||
      (schema.pattern && !new RegExp(schema.pattern).test(value))
    )
      issue("Invalid text length or format");
  } else if (schema.type === "boolean" && typeof value !== "boolean")
    issue("Expected boolean");
  return issues;
}
export function assertValid(schema, value) {
  assertSafe(value);
  const issues = validate(schema, value);
  if (issues.length) throw new ValidationError(issues);
  return value;
}
export const enumField = (values, value = values[0], description = "") => ({
  type: "string",
  enum: values,
  default: value,
  description,
});
export const numberField = (
  value,
  minimum = -1e9,
  maximum = 1e9,
  description = "",
) => ({ type: "number", default: value, minimum, maximum, description });
export const integerField = (value, minimum = 0, maximum = 100) => ({
  type: "integer",
  default: value,
  minimum,
  maximum,
});
export const booleanField = (value = true) => ({
  type: "boolean",
  default: value,
});
export const textField = (value = "", maxLength = 120) => ({
  type: "string",
  default: value,
  maxLength,
});
export const objectField = (properties, value = {}, required = []) => ({
  type: "object",
  properties,
  additionalProperties: false,
  default: value,
  required,
});
export const arrayField = (items, value = [], maxItems = 8) => ({
  type: "array",
  items,
  default: value,
  maxItems,
});
export const stringList = (value = [], maxItems = 8) =>
  arrayField(textField("", 120), value, maxItems);
export const boundedMap = (items, value = {}, maxProperties = 32) => ({
  type: "object",
  additionalProperties: items,
  default: value,
  maxProperties,
});
